import { GraphQLClient } from 'graphql-request';
import { getOktaTokens } from '../getOktaTokens';
import { signOutUser } from '../../auth/signOutUser';
import axios, { AxiosResponse } from 'axios';
import { corpLocationData } from '../../config/locationConfig';
import {
  FormLayout,
  SupportCategoriesAssetQueryVariables,
  SupportCategory,
  Asset,
  AssetResourcesQuery,
  AssetResourcesQueryVariables,
  AssetsQuery,
  AssetsQueryVariables,
  AssetsRecentQuery,
  AssetsRecentQueryVariables,
  AssetWarrantiesQuery,
  AssetWarrantiesQueryVariables,
  AssetWarranty,
  MarkAssetViewedMutation,
  MarkAssetViewedMutationVariables,
  FlowInfosQuery,
  FlowInfosQueryVariables,
  FlowQuery,
  FlowQueryVariables,
  NextFlowStepQuery,
  NextFlowStepQueryVariables,
  TroubleshootingFlowInfo,
  SearchAllSupportQuery,
  SearchAllSupportQueryVariables,
  SearchesRecentQuery,
  SearchesRecentQueryVariables,
  DeleteRecentSearchesMutation,
  DeleteRecentSearchesMutationVariables,
  KbArticlesAnswersQuery,
  KbArticleQuery,
  KbArticleQueryVariables,
  KbFeedbackReasonsQuery,
  SubmitKBFeedbackMutation,
  SubmitKBFeedbackMutationVariables,
  AppointmentsQuery,
  Appointment,
  AppointmentsQueryVariables,
  AppointmentQuery,
  AppointmentQueryVariables,
  OpenCasesQueryVariables,
  CasesQueryVariables,
  TicketQuery,
  TicketQueryVariables,
  MarkCaseReadMutationVariables,
  MarkCaseReadMutation,
  CloseCaseMutation,
  CloseCaseMutationVariables,
  ChangeCaseStatusMutation,
  ChangeCaseStatusMutationVariables,
  ChangeCaseContactMutation,
  ChangeCaseContactMutationVariables,
  RestaurantQuery,
  RestaurantQueryVariables,
  RequestCaseUpdateMutation,
  RequestCaseUpdateMutationVariables,
  AddCommentMutation,
  AddCommentMutationVariables,
  DeleteAttachmentMutation,
  DeleteAttachmentMutationVariables,
  Option,
  ManageCaseSubscriptionMutation,
  ManageCaseSubscriptionMutationVariables,
  CasePromptsQuery,
  CasePromptsQueryVariables,
  SubmitCasePromptMutation,
  SubmitCasePromptMutationVariables,
  RelatedCasesQuery,
  RelatedCasesQueryVariables,
  ChangeNotificationPreferenceMutation,
  ChangeNotificationPreferenceMutationVariables,
  TrackUserEventMutation,
  TrackUserEventMutationVariables,
  UserEventInfoQuery,
  UserEventInfoQueryVariables,
  StaffRestaurants,
  Restaurant,
  AirshipUserProperties,
  ProgramMembershipsQueryVariables,
  ProgramMembershipsQuery,
  ManageProgramMembershipMutationVariables,
  ManageProgramMembershipMutation,
  AssetServiceHistoryQueryVariables,
  AssetServiceHistoryQuery,
  ClearUserEventInfoMutationVariables,
  ClearUserEventInfoMutation,
  CreateServiceEventMutationVariables,
  DeleteAssetServiceEventMutationVariables,
  DeleteAssetServiceEventMutation,
  DeleteAssetServiceAttachmentsMutationVariables,
  DeleteAssetServiceAttachmentsMutation,
  UpdateAssetServiceEventMutationVariables,
  UpdateAssetServiceEventMutation,
  AssetServiceEventQueryVariables,
  AssetServiceEventQuery,
  SupportAppConfigQuery,
  SearchCasesQueryVariables,
  SearchCasesQuery,
  FacilityCategoriesQueryVariables,
  FacilityCategoriesQuery,
  CasesRecentQueryVariables,
  CasesRecentQuery,
  SubmitSearchFeedbackMutation,
  SubmitSearchFeedbackMutationVariables,
  SubmitFormToCreateCaseMutation,
} from '../../../API';
import * as queryOps from './queries';
import * as mutationOps from './mutations';
import {
  afterThisWeek,
  isFutureDatedWithCutoff,
} from '../../date-time/formatDate';
import { handleAddAttachments } from '../../Attachments/handleAddAttachments';
import { AlertInterface } from '../../../interfaces/AlertInterface';
import { FormVariable } from '../../../interfaces/SupportFormInterface';
import { SupportCategoriesInterface } from '../../../interfaces/SupportCategoriesInterface';
import { FileWithPreview } from '../../../interfaces/AttachmentInterface';
import { sendBug } from '../../BugSnag/BugSnagUtilities';
import { ErrorInterface } from '../../../interfaces/ErrorInterface';
import { CleanedAppointment } from '../../../components/Appointments/Appointments';
import { validateTokenExp } from '../../auth/tokenManager';
import { renewIdToken } from '../../auth/renewIdToken';
import { AUTH_RENEWED_RETRY_MESSAGE } from '../authErrors';
import { getViteAppEsrApiUrl } from '../../Vite/getViteAppEsrApiUrl';
import { getViteAppServiceNowDomain } from '../../Vite/getViteAppServiceNowDomain';

const API_URL: string = getViteAppEsrApiUrl() + '/graphql';

const staticHeaders = {
  'CFA-App-Name': 'Support Web',
  'CFA-App-Version': __APP_VERSION__,
};

/**
 * Resolves the idToken to send on a request, handling token lifecycle
 * pre-flight:
 * - Missing token -> sign out (token-missing) and throw.
 * - Expired token -> attempt a renewal - on success return the fresh token, on
 *   failure sign out (token-expired) and throw.
 * - Valid token -> return it as-is.
 *
 * Throwing rejects the outgoing request; the thrown message matches
 * AUTH_ERROR_MESSAGES so React Query skips retries.
 */
export const resolveAuthToken = async (): Promise<string> => {
  let { idToken } = getOktaTokens();

  if (!idToken) {
    // Don't await - let the throw propagate immediately while sign-out runs in the background.
    void signOutUser({ reason: 'token-missing' });
    throw new Error('Missing ID Token');
  }

  if (!validateTokenExp(idToken)) {
    const renewed = await renewIdToken();
    if (!renewed) {
      void signOutUser({ reason: 'token-expired' });
      throw new Error('Token expired or invalid');
    }
    // Re-read from the token store - renewIdToken writes the fresh token there rather than returning it.
    ({ idToken } = getOktaTokens());
    if (!idToken) {
      void signOutUser({ reason: 'token-missing' });
      throw new Error('Missing ID Token');
    }
  }

  return idToken;
};

const graphQLClient = new GraphQLClient(API_URL, {
  headers: staticHeaders,
  requestMiddleware: async request => {
    const headers = new Headers(request.headers);
    headers.set('Authorization', await resolveAuthToken());

    return { ...request, headers };
  },
});

/**
 * Recovers the data a partially failed GraphQL response still carried, since
 * graphql-request throws on any `errors` entry — undefined when there is
 * nothing usable or when it's an auth error handleRequestError must see.
 */
export function partialDataFromError<T>(
  requestError: unknown,
  rootField: string
): T | undefined {
  const response = (requestError as ErrorInterface)?.response;
  const errors = response?.errors;

  if (!errors?.length) return undefined;
  if (errors.some(error => error?.errorType === 'UnauthorizedException')) {
    return undefined;
  }

  return (response?.data?.[rootField] as T | null | undefined) ?? undefined;
}

/**
 * Reports a recovered partial response so the underlying resolver failure
 * stays visible even though the page rendered successfully.
 */
function reportPartialResponse(
  requestId: string,
  requestError: unknown,
  rootField: string
) {
  const errors = (requestError as ErrorInterface)?.response?.errors;
  sendBug(
    `Partial Response: ${requestId} returned ${rootField} with errors ${JSON.stringify(
      errors
    )}`,
    'warning',
    requestId
  );
}

export async function handleRequestError(
  requestId: string,
  requestError: ErrorInterface
) {
  const error = requestError.response?.errors?.[0];

  if (error?.errorType === 'UnauthorizedException') {
    const { idToken } = getOktaTokens();
    const tokenExpired = !idToken || !validateTokenExp(idToken);

    // Token is still valid but the server rejected it: surface the generic
    // error on screen (reject with the original shape) — no renewal, no retry,
    // no sign-out.
    if (!tokenExpired) {
      return Promise.reject({
        response: {
          status: requestError.response?.status,
          errors: [{ ...error }],
        },
      });
    }

    // Token is expired: try to renew, then re-check.
    const renewed = await renewIdToken();
    if (!renewed) {
      // Still expired after renewal — sign the user out. The auth error message
      // is in AUTH_ERROR_MESSAGES so React Query skips retries.
      void signOutUser({ reason: 'token-expired' });
      return Promise.reject(new Error('Token expired or invalid'));
    }

    // Renewed successfully — reject with a retryable marker so React Query
    // re-runs the query, which will send the fresh token.
    return Promise.reject(new Error(AUTH_RENEWED_RETRY_MESSAGE));
  }

  sendBug(
    `Request Failure: ${JSON.stringify(error) ?? ''}`,
    'error',
    requestId
  );
  return Promise.reject({
    ...(requestError.code && { code: requestError.code }),
    response: {
      // Preserve the HTTP status so callers can classify retryability
      // (see isRetryableError) alongside the GraphQL errorType.
      status: requestError.response?.status,
      errors: [{ ...error }],
    },
  });
}

const fetchTickets = async (
  query: string,
  variables: Record<string, unknown>,
  errorContext: string,
  // Key to extract the main data from the GraphQL response (e.g., 'cases', 'closedCases', 'openCases')
  dataKey: string
) => {
  try {
    const response: Record<string, unknown> = await graphQLClient.request(
      query,
      variables
    );
    interface TicketResponse {
      pageInfo?: { hasNextPage?: boolean; totalPages?: number };
      cases?: unknown[];
    }

    const data = response[dataKey] as TicketResponse;

    // Early return if we don't need page 2 data
    const needsPage2Data =
      variables.page === 1 &&
      data?.pageInfo?.hasNextPage &&
      !data?.pageInfo?.totalPages;

    if (!needsPage2Data) {
      return data;
    }

    // Fetch page 2 for complete pagination info
    const page2Response: Record<string, unknown> = await graphQLClient.request(
      query,
      { ...variables, page: 2 }
    );

    return {
      ...data,
      pageInfo: {
        ...(page2Response[dataKey] as TicketResponse)?.pageInfo,
      },
    };
  } catch (requestError) {
    return handleRequestError(
      `getTickets - ${errorContext}`,
      requestError as ErrorInterface
    );
  }
};

export const getTickets = async (
  locationNum: CasesQueryVariables['locationNum'],
  page: CasesQueryVariables['page'],
  user: OpenCasesQueryVariables['contactGuid'],
  filter: string
) => {
  const variables = { locationNum, page };

  switch (filter) {
    case 'open':
      return fetchTickets(
        queryOps.GET_OPEN_TICKETS,
        variables,
        'open tickets',
        'openCases'
      );

    case 'my_open':
      return fetchTickets(
        queryOps.GET_OPEN_TICKETS_BY_USER,
        { ...variables, user },
        'my open tickets',
        'openCases'
      );

    case 'closed':
      return fetchTickets(
        queryOps.GET_CLOSED_TICKETS,
        variables,
        'closed tickets',
        'closedCases'
      );

    case 'all':
      return fetchTickets(
        queryOps.GET_ALL_TICKETS,
        variables,
        'all tickets',
        'cases'
      );

    default:
      return Promise.reject(new Error('Invalid Filter Value'));
  }
};

export const getTicketLocation = async (args: TicketQueryVariables) => {
  try {
    const response: { case: TicketQuery['ticket'] } =
      await graphQLClient.request(queryOps.GET_TICKET_LOCATION_BY_ID, {
        ...args,
      });

    if (!response.case) {
      throw new Error(`Ticket location not found for ID: ${args.id}`);
    }

    return response.case;
  } catch (requestError) {
    return handleRequestError(
      'GetTicketLocation',
      requestError as ErrorInterface
    );
  }
};

export const getTicket = async (args: TicketQueryVariables) => {
  try {
    const GetTicket: { case: TicketQuery['ticket'] } =
      await graphQLClient.request(queryOps.GET_TICKET_BY_ID, {
        ...args,
      });
    return GetTicket.case;
  } catch (requestError) {
    const partialCase = partialDataFromError<TicketQuery['ticket']>(
      requestError,
      'case'
    );
    if (partialCase) {
      reportPartialResponse('getTicket', requestError, 'case');
      return partialCase;
    }
    return handleRequestError('getTicket', requestError as ErrorInterface);
  }
};

export const getLocation = async (
  locationNum: RestaurantQueryVariables['locationNum'],
  token?: Nullable<string>
) => {
  try {
    if (locationNum === '00000') return corpLocationData;
    token && graphQLClient.setHeader('Authorization', token);
    const { restaurant }: RestaurantQuery = await graphQLClient.request(
      queryOps.GET_LOCATION,
      {
        locationNum,
      }
    );
    if (!restaurant) {
      throw new Error(`Restaurant ${locationNum} returned null`);
    }
    return restaurant;
  } catch (requestError) {
    return handleRequestError('getLocation', requestError as ErrorInterface);
  }
};

export const getStaffLocations = async (userId: string, isAdmin: boolean) => {
  try {
    const { restaurantsStaff }: { restaurantsStaff: StaffRestaurants } =
      await graphQLClient.request(queryOps.GET_STAFF_LOCATIONS, {
        guid: userId,
      });
    const filteredLocations = isAdmin
      ? restaurantsStaff.restaurants
      : restaurantsStaff.restaurants.filter(loc => loc?.number !== '00000');

    if (restaurantsStaff.type === 'RECENT')
      return {
        type: restaurantsStaff.type,
        restaurants: [...filteredLocations],
      };

    return {
      type: restaurantsStaff.type,
      restaurants: filteredLocations.sort(function (a, b) {
        if (!a || !b) return 0;
        if ((a.name ?? '') < (b.name ?? '')) {
          return -1;
        }
        if ((a.name ?? '') > (b.name ?? '')) {
          return 1;
        }
        return 0;
      }),
    };
  } catch (requestError) {
    return handleRequestError(
      'getStaffLocations',
      requestError as ErrorInterface
    );
  }
};

export const getAllLocations = async (isAdmin: boolean) => {
  let sortLocations;
  try {
    const { restaurants }: { restaurants: Restaurant[] } =
      await graphQLClient.request(queryOps.GET_ALL_LOCATIONS);

    sortLocations = function (restaurants: Restaurant[]) {
      return restaurants.sort(function (a: Restaurant, b: Restaurant) {
        const aName = a.name ?? '';
        const bName = b.name ?? '';
        if (aName < bName) {
          return -1;
        }
        if (aName > bName) {
          return 1;
        }
        return 0;
      });
    };

    return sortLocations(
      isAdmin
        ? [
            ...restaurants,
            { ...corpLocationData, __typename: 'Restaurant' } as Restaurant,
          ]
        : restaurants
    );
  } catch (requestError) {
    return handleRequestError(
      'getAllLocations',
      requestError as ErrorInterface
    );
  }
};

export const markLocationAsVisited = async (
  locationNum?: string,
  locationName?: string
) => {
  if (!locationNum || !locationName) {
    throw new Error('Location name and number are required');
  }
  try {
    const MarkVisited: { markRestaurantVisited: string } =
      await graphQLClient.request(mutationOps.MARK_LOCATION_VISITED, {
        locationNum,
        locationName,
      });
    return MarkVisited;
  } catch (requestError) {
    return handleRequestError(
      'markLocationAsVisited',
      requestError as ErrorInterface
    );
  }
};

export const markTicketAsRead = async (args: MarkCaseReadMutationVariables) => {
  try {
    const { markCaseRead }: MarkCaseReadMutation = await graphQLClient.request(
      mutationOps.MARK_TICKET_READ,
      {
        ...args,
      }
    );
    return markCaseRead;
  } catch (requestError) {
    return handleRequestError(
      'markTicketAsRead',
      requestError as ErrorInterface
    );
  }
};

export const closeTicket = async (args: CloseCaseMutationVariables) => {
  try {
    const { closeCase }: CloseCaseMutation = await graphQLClient.request(
      mutationOps.CLOSE_TICKET,
      {
        ...args,
      }
    );
    return closeCase;
  } catch (requestError) {
    return handleRequestError('closeTicket', requestError as ErrorInterface);
  }
};

export const changeTicketStatus = async (
  args: ChangeCaseStatusMutationVariables
) => {
  try {
    const { changeCaseStatus }: ChangeCaseStatusMutation =
      await graphQLClient.request(mutationOps.CHANGE_TICKET_STATUS, {
        ...args,
      });
    return changeCaseStatus;
  } catch (requestError) {
    return handleRequestError(
      'changeTicketStatus',
      requestError as ErrorInterface
    );
  }
};

export const changeTicketContact = async (
  args: ChangeCaseContactMutationVariables
) => {
  try {
    const { changeCaseContact }: ChangeCaseContactMutation =
      await graphQLClient.request(mutationOps.CHANGE_TICKET_CONTACT, {
        ...args,
      });
    return changeCaseContact;
  } catch (requestError) {
    return handleRequestError(
      'changeTicketContact',
      requestError as ErrorInterface
    );
  }
};

export const requestTicketUpdate = async (
  args: RequestCaseUpdateMutationVariables
) => {
  try {
    const { requestCaseUpdate }: RequestCaseUpdateMutation =
      await graphQLClient.request(mutationOps.REQUEST_CASE_UPDATE, {
        ...args,
      });
    return requestCaseUpdate;
  } catch (requestError) {
    return handleRequestError(
      'requestTicketUpdate',
      requestError as ErrorInterface
    );
  }
};

export const addTicketComment = async (args: AddCommentMutationVariables) => {
  try {
    const { addComment }: AddCommentMutation = await graphQLClient.request(
      mutationOps.ADD_TICKET_COMMENT,
      {
        ...args,
      }
    );
    return addComment;
  } catch (requestError) {
    const partialComment = partialDataFromError<
      AddCommentMutation['addComment']
    >(requestError, 'addComment');
    if (partialComment) {
      reportPartialResponse('addTicketComment', requestError, 'addComment');
      return partialComment;
    }
    return handleRequestError(
      'addTicketComment',
      requestError as ErrorInterface
    );
  }
};

export const deleteTicketAttachment = async (
  args: DeleteAttachmentMutationVariables
) => {
  try {
    const { deleteAttachment }: DeleteAttachmentMutation =
      await graphQLClient.request(mutationOps.DELETE_ATTACHMENT, {
        ...args,
      });
    return deleteAttachment;
  } catch (requestError) {
    return handleRequestError(
      'deleteTicketAttachment',
      requestError as ErrorInterface
    );
  }
};

export const getCloseTicketReasons = async () => {
  try {
    const data: {
      closeCaseReasons: [{ label: Option['label']; value: Option['value'] }];
    } = await graphQLClient.request(queryOps.GET_CLOSE_REASONS);
    return data.closeCaseReasons;
  } catch (requestError) {
    return handleRequestError(
      'getCloseTicketReasons',
      requestError as ErrorInterface
    );
  }
};

export const fetchAttachment = async (downloadURL: string): Promise<string> => {
  const { idToken: ID_TOKEN } = getOktaTokens();
  if (!ID_TOKEN) {
    throw new Error('Missing ID Token');
  }

  try {
    const urlResponse = await fetch(downloadURL, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: 'Bearer ' + ID_TOKEN,
      },
    });

    const urlBlob = await urlResponse.blob();
    return URL.createObjectURL(urlBlob);
  } catch (requestError) {
    return handleRequestError(
      'fetchAttachment',
      requestError as ErrorInterface
    );
  }
};

export const getAnnouncements = async () => {
  try {
    const GetAlerts: { announcements: AlertInterface[] } =
      await graphQLClient.request(queryOps.GET_ANNOUNCEMENTS);

    // Major-case announcements come first; within each group the oldest
    // announcement leads.
    const sortAlerts = (alerts: AlertInterface[]) =>
      alerts.sort((a, b) => {
        const aIsMajorCase = !!a.majorCaseId;
        const bIsMajorCase = !!b.majorCaseId;
        if (aIsMajorCase !== bIsMajorCase) return aIsMajorCase ? -1 : 1;
        return (
          new Date(a.updatedOn).valueOf() - new Date(b.updatedOn).valueOf()
        );
      });

    const activeAnnouncements = sortAlerts(
      GetAlerts.announcements.filter(alert => !alert.isExpired)
    );

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const expiredAnnouncements = GetAlerts.announcements
      .filter(
        alert => alert.isExpired && new Date(alert.updatedOn) >= thirtyDaysAgo
      )
      .sort(
        (a, b) =>
          new Date(b.updatedOn).valueOf() - new Date(a.updatedOn).valueOf()
      );

    return {
      activeAnnouncements,
      expiredAnnouncements,
    };
  } catch (requestError) {
    return handleRequestError(
      'getAnnouncements',
      requestError as ErrorInterface
    );
  }
};

export const getSupportAppConfig = async () => {
  try {
    const { supportAppConfig }: SupportAppConfigQuery =
      await graphQLClient.request(queryOps.GET_SUPPORT_APP_CONFIG);

    return supportAppConfig;
  } catch (requestError) {
    return handleRequestError(
      'getSupportAppConfig',
      requestError as ErrorInterface
    );
  }
};

export const getSupportCategories = async () => {
  try {
    const {
      supportCategories,
    }: {
      supportCategories: SupportCategoriesInterface[];
    } = await graphQLClient.request(queryOps.GET_SUPPORT_CATEGORIES);

    const supportItems = new Set<SupportCategory['id']>(); // temp variable to keep track of accepted ids
    const uniqueSupportItems = supportCategories
      .flatMap(category => category.items)
      .filter(({ id }) => !supportItems.has(id) && supportItems.add(id));

    const commonItems = uniqueSupportItems
      .filter(item => item.commonlyUsedOrder != null)
      .sort((a, b) => {
        return (a?.commonlyUsedOrder ?? 0) - (b?.commonlyUsedOrder ?? 0);
      });

    const featuredItems = uniqueSupportItems
      .filter(item => item.featuredOrder != null)
      .sort((a, b) => {
        return (a?.featuredOrder ?? 0) - (b?.featuredOrder ?? 0);
      });

    const recommendedForms = uniqueSupportItems
      .filter(item => item.recommendedOrder != null)
      .sort((a, b) => {
        return (a?.recommendedOrder ?? 0) - (b?.recommendedOrder ?? 0);
      });

    const featuredForms = uniqueSupportItems
      .filter(item => item.recentlyFeaturedOrder != null)
      .sort((a, b) => {
        return (
          (a?.recentlyFeaturedOrder ?? 0) - (b?.recentlyFeaturedOrder ?? 0)
        );
      });

    return {
      supportCategories,
      commonItems,
      featuredItems,
      recommendedForms,
      featuredForms,
    };
  } catch (requestError) {
    return handleRequestError(
      'getSupportCategories',
      requestError as ErrorInterface
    );
  }
};

export const getSupportCategoryItem = async (id: string) => {
  try {
    const {
      supportCategoryItem,
    }: {
      supportCategoryItem: SupportCategory;
    } = await graphQLClient.request(queryOps.GET_SUPPORT_CATEGORY_ITEM, {
      id,
    });

    return supportCategoryItem;
  } catch (requestError) {
    return handleRequestError(
      'getSupportCategoryItem',
      requestError as ErrorInterface
    );
  }
};

export const getFormLayout = async (
  location: string,
  id: string,
  defaults?: { id: string; value: string | undefined }[]
) => {
  if (id === undefined || id == '') {
    throw new Error('Missing Form Id');
  }

  try {
    const GetFormLayout: {
      formLayout: FormLayout;
    } = await graphQLClient.request(queryOps.GET_FORM_LAYOUT, {
      id,
      locationNum: location,
      defaults: defaults ?? [],
    });

    return GetFormLayout.formLayout;
  } catch (requestError) {
    return handleRequestError('getFormLayout', requestError as ErrorInterface);
  }
};

export const searchAllSupport = async (
  args: SearchAllSupportQueryVariables
) => {
  try {
    const { searchAllSupport }: SearchAllSupportQuery =
      await graphQLClient.request(queryOps.SEARCH_ALL_SUPPORT, {
        ...args,
      });

    return searchAllSupport;
  } catch (requestError) {
    return handleRequestError(
      'searchAllSupport',
      requestError as ErrorInterface
    );
  }
};

export const getServiceNowLink = async (link: string) => {
  try {
    const { secureServiceNowLink }: { secureServiceNowLink: string } =
      await graphQLClient.request(queryOps.GET_SECURE_SNOW_URL, {
        link,
      });

    return secureServiceNowLink;
  } catch (requestError) {
    return handleRequestError(
      'getServiceNowLink',
      requestError as ErrorInterface
    );
  }
};

export const postAttachment = async (
  file: FileWithPreview,
  ticketId?: string
): Promise<AxiosResponse<void>> => {
  const { idToken: ID_TOKEN } = getOktaTokens();
  if (!ID_TOKEN) {
    throw new Error('Missing ID Token');
  }

  if (!ticketId) {
    throw new Error('Missing ticket ID');
  }

  try {
    const getPostUrl = (fileName: string) => {
      return `${getViteAppServiceNowDomain()}/api/now/attachment/file?table_name=sn_customerservice_case&table_sys_id=${ticketId}&file_name=${fileName}`;
    };

    return await axios.post(getPostUrl(file.name), file, {
      headers: {
        'Content-type': 'application/octet-stream',
        Authorization: 'Bearer ' + ID_TOKEN,
      },
    });
  } catch (requestError) {
    return handleRequestError('postAttachment', requestError as ErrorInterface);
  }
};

// simulation counter
//let _simUploadCall = 0;

export const uploadServiceEventAttachment = async (
  file: FileWithPreview,
  uploadUrl: string
): Promise<AxiosResponse<void>> => {
  // _simUploadCall += 1;
  // const shouldFail = [2, 3, 5, 7].includes(_simUploadCall);
  // console.log(
  //   `[SIM] upload #${_simUploadCall} — ${file.name} — ${
  //     shouldFail ? 'FAIL' : 'PASS'
  //   }`
  // );
  // if (shouldFail) {
  //   throw new Error('Simulated upload failure');
  // }

  const { idToken: ID_TOKEN } = getOktaTokens();
  if (!ID_TOKEN) {
    throw new Error('Missing ID Token');
  }

  try {
    return await axios.post(uploadUrl, file, {
      headers: {
        'Content-type': file.type || 'application/octet-stream',
        Authorization: 'Bearer ' + ID_TOKEN,
      },
    });
  } catch (requestError) {
    return handleRequestError(
      'uploadServiceEventAttachment',
      requestError as ErrorInterface
    );
  }
};

export const submitFormToCreateCase = async (
  locationNum: string,
  formID: string,
  variables: FormVariable[],
  formTitle?: FormLayout['title'],
  attachments?: FileWithPreview[]
) => {
  try {
    const {
      submitFormToCreateCase,
    }: {
      submitFormToCreateCase: SubmitFormToCreateCaseMutation['submitFormToCreateCase'];
    } = await graphQLClient.request(mutationOps.SUBMIT_FORM_TO_CREATE_CASE, {
      locationNum,
      formID,
      variables,
    });

    const confirmationMessages =
      submitFormToCreateCase.__typename === 'SubmitFormResponseType'
        ? submitFormToCreateCase.confirmationMessages
        : undefined;

    if (attachments && attachments.length > 0) {
      const formData = { formID: formID, formTitle: formTitle };
      const attachmentErrors = await handleAddAttachments(
        attachments,
        submitFormToCreateCase.id,
        submitFormToCreateCase.number,
        formData
      );
      return {
        ...submitFormToCreateCase,
        confirmationMessages,
        attachmentErrors,
      };
    }

    return {
      ...submitFormToCreateCase,
      confirmationMessages,
      attachmentErrors: [],
    };
  } catch (requestError) {
    return handleRequestError(
      'submitFormToCreateCase',
      requestError as ErrorInterface
    );
  }
};

export const getRelatedMinorCases = async (
  args: RelatedCasesQueryVariables
) => {
  try {
    //always send as { caseId: majorCaseId }
    const { relatedCases }: RelatedCasesQuery = await graphQLClient.request(
      queryOps.GET_RELATED_MINOR_CASES,
      {
        caseId: args.majorCaseId,
      }
    );

    return relatedCases;
  } catch (requestError) {
    return handleRequestError(
      'getRelatedMinorCases',
      requestError as ErrorInterface
    );
  }
};

export const getKBArticle = async (args: KbArticleQueryVariables) => {
  try {
    const { kbArticle }: KbArticleQuery = await graphQLClient.request(
      queryOps.GET_KB_ARTICLE,
      {
        ...args,
      }
    );
    return kbArticle;
  } catch (requestError) {
    return handleRequestError('getKBArticle', requestError as ErrorInterface);
  }
};

export const getRecentSearches = async (args: SearchesRecentQueryVariables) => {
  try {
    const { searchesRecent }: SearchesRecentQuery = await graphQLClient.request(
      queryOps.GET_RECENT_SEARCHES,
      {
        ...args,
      }
    );
    return searchesRecent.filter((result): result is string => !!result);
  } catch (requestError) {
    return handleRequestError(
      'getRecentSearches',
      requestError as ErrorInterface
    );
  }
};

export const getHelpfulRecentKBArticles = async () => {
  try {
    const { kbArticlesAnswers }: KbArticlesAnswersQuery =
      await graphQLClient.request(queryOps.GET_HELPFUL_RECENT_KB_ARTICLES);

    return kbArticlesAnswers;
  } catch (requestError) {
    return handleRequestError(
      'getHelpfulRecentKBArticles',
      requestError as ErrorInterface
    );
  }
};

export const deleteRecentSearch = async (
  args: DeleteRecentSearchesMutationVariables
) => {
  try {
    const { deleteRecentSearches }: DeleteRecentSearchesMutation =
      await graphQLClient.request(mutationOps.DELETE_RECENT_SEARCH, {
        ...args,
      });
    return deleteRecentSearches;
  } catch (requestError) {
    return handleRequestError(
      'deleteRecentSearch',
      requestError as ErrorInterface
    );
  }
};

export const getAppointments = async (
  args: AppointmentsQueryVariables,
  isHomePage?: boolean
) => {
  try {
    const { appointments }: AppointmentsQuery = await graphQLClient.request(
      queryOps.GET_APPOINTMENTS,
      {
        ...args,
      }
    );

    const filteredAppointments = appointments.filter(
      (appt): appt is CleanedAppointment => !!appt && !!appt.dueDate
    );
    if (isHomePage && args.locationNums) {
      const locations = args.locationNums?.filter(loc => !!loc) ?? [];
      return filteredAppointments.filter(
        ({ restaurant, status, dueDate }) =>
          locations.includes(restaurant?.number ?? '') &&
          status !== 'COMPLETED' &&
          isFutureDatedWithCutoff(3, dueDate) &&
          !afterThisWeek(dueDate)
      );
    }
    return filteredAppointments;
  } catch (requestError) {
    return handleRequestError(
      'getAppointmentsManagementPage',
      requestError as ErrorInterface
    );
  }
};

export const getAppointmentDetails = async (
  args: AppointmentQueryVariables
): Promise<CleanedAppointment | undefined> => {
  try {
    const { appointment }: AppointmentQuery = await graphQLClient.request(
      queryOps.GET_APPOINTMENT_DETAILS,
      {
        ...args,
      }
    );

    return appointment?.dueDate
      ? (appointment as CleanedAppointment)
      : undefined;
  } catch (error) {
    // A failure with no response (e.g. a network error) has no partial data.
    const data = (
      error as {
        response?: {
          data?: {
            appointment?: Appointment;
          };
        };
      }
    )?.response?.data;
    if (data && data.appointment?.dueDate) {
      return Promise.resolve(data.appointment as CleanedAppointment);
    }
    return handleRequestError('getAppointmentDetails', error as ErrorInterface);
  }
};

export const getKBFeedbackReasons = async () => {
  try {
    const { kbFeedbackReasons }: KbFeedbackReasonsQuery =
      await graphQLClient.request(queryOps.GET_KB_FEEDBACK_REASONS);
    return kbFeedbackReasons;
  } catch (requestError) {
    return handleRequestError(
      'getKBFeedbackReasons',
      requestError as ErrorInterface
    );
  }
};

export const submitKBFeedback = async (
  args: SubmitKBFeedbackMutationVariables
) => {
  try {
    const { submitKBFeedback }: SubmitKBFeedbackMutation =
      await graphQLClient.request(mutationOps.SUBMIT_KB_FEEDBACK, {
        ...args,
      });
    return submitKBFeedback;
  } catch (requestError) {
    return handleRequestError(
      'submitKBFeedback',
      requestError as ErrorInterface
    );
  }
};

export const getAssets = async (args: AssetsQueryVariables) => {
  try {
    const { assets: allEquipment }: AssetsQuery = await graphQLClient.request(
      queryOps.GET_ASSETS,
      {
        ...args,
      }
    );

    const [restaurantEquipment, technologyEquipment] = allEquipment.reduce(
      ([restaurantEquipment, technologyEquipment], asset) => {
        return asset?.department === 'RD'
          ? [[...restaurantEquipment, asset], technologyEquipment]
          : [restaurantEquipment, [...technologyEquipment, asset]];
      },
      [<AssetsQuery['assets']>[], <AssetsQuery['assets']>[]]
    );

    const restaurantManufacturers = [
      ...new Set<Asset['manufacturer']>(
        restaurantEquipment.map(asset => asset?.manufacturer)
      ),
    ].filter((mfr): mfr is string => !!mfr);
    const technologyManufacturers = [
      ...new Set<Asset['manufacturer']>(
        technologyEquipment.map(asset => asset?.manufacturer)
      ),
    ].filter((mfr): mfr is string => !!mfr);

    return {
      allEquipment: allEquipment.filter(asset => !!asset),
      restaurantEquipment: restaurantEquipment.filter(asset => !!asset),
      technologyEquipment: technologyEquipment.filter(asset => !!asset),
      restaurantManufacturers,
      technologyManufacturers,
    };
  } catch (requestError) {
    return handleRequestError('getAssets', requestError as ErrorInterface);
  }
};

export const getRecentlyViewedAssets = async (
  guid: AssetsRecentQueryVariables['userGuid']
) => {
  try {
    const { assetsRecent }: AssetsRecentQuery = await graphQLClient.request(
      queryOps.GET_RECENT_ASSETS,
      {
        guid,
      }
    );

    return assetsRecent;
  } catch (requestError) {
    return handleRequestError(
      'getRecentlyViewedAssets',
      requestError as ErrorInterface
    );
  }
};

export const getAssetResources = async (args: AssetResourcesQueryVariables) => {
  try {
    const { assetResources }: AssetResourcesQuery = await graphQLClient.request(
      queryOps.GET_ASSET_RESOURCES,
      {
        ...args,
      }
    );
    return assetResources;
  } catch (requestError) {
    return handleRequestError(
      'getAssetResources',
      requestError as ErrorInterface
    );
  }
};

export const getSupportCategoriesAsset = async (
  args: SupportCategoriesAssetQueryVariables
) => {
  try {
    const {
      supportCategoriesAsset,
    }: {
      supportCategoriesAsset: SupportCategory[];
    } = await graphQLClient.request(queryOps.GET_SUPPORT_CATEGORIES_ASSET, {
      ...args,
    });

    return supportCategoriesAsset;
  } catch (requestError) {
    return handleRequestError(
      'getSupportCategoriesAsset',
      requestError as ErrorInterface
    );
  }
};

export const markAssetViewed = async (
  input: MarkAssetViewedMutationVariables['input']
) => {
  try {
    const { markAssetViewed }: MarkAssetViewedMutation =
      await graphQLClient.request(mutationOps.MARK_ASSET_VIEWED, {
        input,
      });
    return markAssetViewed;
  } catch (requestError) {
    return handleRequestError(
      'markAssetViewed',
      requestError as ErrorInterface
    );
  }
};

export const updateCaseSubscription = async (
  args: ManageCaseSubscriptionMutationVariables
) => {
  try {
    const { manageCaseSubscription }: ManageCaseSubscriptionMutation =
      await graphQLClient.request(mutationOps.SUBSCRIBE_UNSUBSCRIBE_CASE, {
        ...args,
      });

    return manageCaseSubscription;
  } catch (requestError) {
    return handleRequestError(
      'updateCaseSubscription',
      requestError as ErrorInterface
    );
  }
};

export const getAirshipUserProperties = async (guid: string) => {
  try {
    const {
      airshipUserProperties: { tagGroups },
    }: {
      airshipUserProperties: AirshipUserProperties;
    } = await graphQLClient.request(queryOps.GET_AIRSHIP_USER_PROPERTIES, {
      guid,
    });

    return tagGroups;
  } catch (requestError) {
    return handleRequestError(
      'getAirshipUserProperties',
      requestError as ErrorInterface
    );
  }
};

export const markUserOnboarding = async (status: string) => {
  try {
    const MarkUserOnboarding: { markUserOnboardingStatus: string } =
      await graphQLClient.request(mutationOps.MARK_USER_ONBOARDING, {
        status,
      });

    return MarkUserOnboarding;
  } catch (requestError) {
    return handleRequestError(
      'markUserOnboarding',
      requestError as ErrorInterface
    );
  }
};

export const changeUserNotificationPreference = async (
  args: ChangeNotificationPreferenceMutationVariables
) => {
  try {
    const {
      changeNotificationPreference,
    }: ChangeNotificationPreferenceMutation = await graphQLClient.request(
      mutationOps.CHANGE_NOTIFICATION_PREFERENCE,
      {
        ...args,
      }
    );

    return changeNotificationPreference;
  } catch (requestError) {
    return handleRequestError(
      'changeUserNotificationPreference',
      requestError as ErrorInterface
    );
  }
};

export const getCasePrompts = async (args: CasePromptsQueryVariables) => {
  try {
    const { casePrompts }: CasePromptsQuery = await graphQLClient.request(
      queryOps.GET_CASE_PROMPTS,
      {
        ...args,
      }
    );

    return casePrompts;
  } catch (requestError) {
    return handleRequestError('getCasePrompts', requestError as ErrorInterface);
  }
};

export const submitCasePrompt = async (
  args: SubmitCasePromptMutationVariables
) => {
  try {
    const { submitCasePrompt }: SubmitCasePromptMutation =
      await graphQLClient.request(mutationOps.SUBMIT_CASE_PROMPT, {
        ...args,
      });

    return submitCasePrompt;
  } catch (requestError) {
    return handleRequestError(
      'submitCasePrompt',
      requestError as ErrorInterface
    );
  }
};

export const getAssetWarranties = async (
  args: AssetWarrantiesQueryVariables
) => {
  try {
    const { assetWarranties }: AssetWarrantiesQuery =
      await graphQLClient.request(queryOps.GET_ASSET_WARRANTIES, { ...args });

    return assetWarranties.filter(
      (warranty): warranty is AssetWarranty => !!warranty
    );
  } catch (requestError) {
    return handleRequestError(
      'getAssetWarranties',
      requestError as ErrorInterface
    );
  }
};

export const getUserEventInfo = async (args: UserEventInfoQueryVariables) => {
  try {
    const { userEventInfo }: UserEventInfoQuery = await graphQLClient.request(
      queryOps.GET_USER_EVENT_INFO,
      {
        ...args,
      }
    );

    return userEventInfo;
  } catch (requestError) {
    return handleRequestError(
      'getUserEventInfo',
      requestError as ErrorInterface
    );
  }
};

export const trackUserEvent = async (args: TrackUserEventMutationVariables) => {
  try {
    const { trackUserEvent }: TrackUserEventMutation =
      await graphQLClient.request(mutationOps.TRACK_USER_EVENT, {
        ...args,
      });
    return trackUserEvent;
  } catch (requestError) {
    return handleRequestError('trackUserEvent', requestError as ErrorInterface);
  }
};

export const getTroubleshootingFlows = async (
  args: FlowInfosQueryVariables
) => {
  try {
    const { flowInfos }: FlowInfosQuery = await graphQLClient.request(
      queryOps.GET_TROUBLESHOOTING_FLOWS,
      { ...args }
    );

    return flowInfos.filter((flow): flow is TroubleshootingFlowInfo => !!flow);
  } catch (requestError) {
    return handleRequestError(
      'getTroubleshootingFlows',
      requestError as ErrorInterface
    );
  }
};

export const getTroubleshootingFlow = async (args: FlowQueryVariables) => {
  try {
    const { flow }: FlowQuery = await graphQLClient.request(
      queryOps.GET_TROUBLESHOOTING_FLOW,
      {
        ...args,
      }
    );

    return flow;
  } catch (requestError) {
    return handleRequestError(
      'getTroubleshootingFlow',
      requestError as ErrorInterface
    );
  }
};

export const getTroubleshootingFlowNextStep = async (
  args: NextFlowStepQueryVariables
) => {
  try {
    const { nextFlowStep }: NextFlowStepQuery = await graphQLClient.request(
      queryOps.GET_TROUBLESHOOTING_FLOW_NEXT_STEP,
      {
        ...args,
      }
    );

    return nextFlowStep;
  } catch (requestError) {
    return handleRequestError(
      'getTroubleshootingFlowNextStep',
      requestError as ErrorInterface
    );
  }
};

export const getMemberships = async (
  args: ProgramMembershipsQueryVariables
) => {
  try {
    const { programMemberships }: ProgramMembershipsQuery =
      await graphQLClient.request(queryOps.GET_MEMBERSHIPS, {
        ...args,
      });

    return programMemberships;
  } catch (requestError) {
    return handleRequestError('getMemberships', requestError as ErrorInterface);
  }
};

export const getServiceHistory = async (
  args: AssetServiceHistoryQueryVariables
) => {
  try {
    const { assetServiceHistory }: AssetServiceHistoryQuery =
      await graphQLClient.request(queryOps.GET_ASSET_SERVICE_HISTORY, {
        ...args,
      });

    return assetServiceHistory;
  } catch (requestError) {
    return handleRequestError(
      'getServiceHistory',
      requestError as ErrorInterface
    );
  }
};

export const getServiceEvent = async (
  args: AssetServiceEventQueryVariables
) => {
  try {
    const { assetServiceEvent }: AssetServiceEventQuery =
      await graphQLClient.request(queryOps.GET_ASSET_SERVICE_EVENT, {
        ...args,
      });

    return assetServiceEvent;
  } catch (requestError) {
    return handleRequestError(
      'getServiceEvent',
      requestError as ErrorInterface
    );
  }
};

export const manageMemberships = async (
  args: ManageProgramMembershipMutationVariables
) => {
  try {
    const { manageProgramMembership }: ManageProgramMembershipMutation =
      await graphQLClient.request(mutationOps.MANAGE_MEMBERSHIPS, {
        ...args,
      });

    return manageProgramMembership;
  } catch (requestError) {
    return handleRequestError(
      'manageProgramMembership',
      requestError as ErrorInterface
    );
  }
};

export const clearUserEventInfo = async (
  eventType: ClearUserEventInfoMutationVariables['eventType']
) => {
  try {
    const { clearUserEventInfo }: ClearUserEventInfoMutation =
      await graphQLClient.request(mutationOps.CLEAR_USER_EVENT_INFO, {
        eventType: eventType,
      });
    return clearUserEventInfo;
  } catch (requestError) {
    return handleRequestError(
      'clearUserEventInfo',
      requestError as ErrorInterface
    );
  }
};

export const createServiceEvent = async (
  input: CreateServiceEventMutationVariables['input']
) => {
  try {
    const response = await graphQLClient.request(
      mutationOps.CREATE_SERVICE_EVENT,
      {
        input,
      }
    );
    return response;
  } catch (requestError) {
    return handleRequestError(
      'createServiceEvent',
      requestError as ErrorInterface
    );
  }
};

export const deleteServiceEvent = async (
  input: DeleteAssetServiceEventMutationVariables['input']
) => {
  try {
    const { deleteAssetServiceEvent }: DeleteAssetServiceEventMutation =
      await graphQLClient.request(mutationOps.DELETE_SERVICE_EVENT, {
        input,
      });
    return deleteAssetServiceEvent;
  } catch (requestError) {
    return handleRequestError(
      'deleteServiceEvent',
      requestError as ErrorInterface
    );
  }
};

export const deleteServiceEventAttachments = async (
  input: DeleteAssetServiceAttachmentsMutationVariables['input']
) => {
  try {
    const {
      deleteAssetServiceAttachments,
    }: DeleteAssetServiceAttachmentsMutation = await graphQLClient.request(
      mutationOps.DELETE_SERVICE_EVENT_ATTACHMENTS,
      {
        input,
      }
    );
    return deleteAssetServiceAttachments;
  } catch (requestError) {
    return handleRequestError(
      'deleteServiceEventAttachments',
      requestError as ErrorInterface
    );
  }
};

export const updateServiceEvent = async (
  input: UpdateAssetServiceEventMutationVariables['input']
) => {
  try {
    const { updateAssetServiceEvent }: UpdateAssetServiceEventMutation =
      await graphQLClient.request(mutationOps.UPDATE_SERVICE_EVENT, {
        input,
      });
    return updateAssetServiceEvent;
  } catch (requestError) {
    return handleRequestError(
      'updateServiceEvent',
      requestError as ErrorInterface
    );
  }
};

export const searchTickets = async (args: SearchCasesQueryVariables) => {
  try {
    const { searchCases }: SearchCasesQuery = await graphQLClient.request(
      queryOps.SEARCH_TICKETS,
      {
        ...args,
      }
    );

    return searchCases;
  } catch (requestError) {
    return handleRequestError('searchCases', requestError as ErrorInterface);
  }
};

export const getFacilityCategories = async (
  args: FacilityCategoriesQueryVariables = {}
) => {
  try {
    const { facilityCategories }: FacilityCategoriesQuery =
      await graphQLClient.request(queryOps.facilityCategories, {
        ...args,
      });

    return facilityCategories;
  } catch (requestError) {
    return handleRequestError(
      'getFacilityCategories',
      requestError as ErrorInterface
    );
  }
};

export const getRecentlyViewedTickets = async (
  locationNums: CasesRecentQueryVariables['locationNums']
) => {
  try {
    const { casesRecent }: CasesRecentQuery = await graphQLClient.request(
      queryOps.GET_CASES_RECENT,
      { locationNums }
    );
    return casesRecent;
  } catch (requestError) {
    return handleRequestError(
      'getRecentlyViewedTickets',
      requestError as ErrorInterface
    );
  }
};

export const submitSearchFeedback = async (
  args: SubmitSearchFeedbackMutationVariables
) => {
  try {
    const { submitSearchFeedback }: SubmitSearchFeedbackMutation =
      await graphQLClient.request(mutationOps.SUBMIT_SEARCH_FEEDBACK, {
        ...args,
      });
    return submitSearchFeedback;
  } catch (requestError) {
    return handleRequestError(
      'submitSearchFeedback',
      requestError as ErrorInterface
    );
  }
};
